import {
  IconButton,
  LinearProgress,
  MenuItem,
  Select,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import dayjs from 'dayjs';
import { KeyboardEvent, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';

import {
  convertEvaluationType,
  convertReportGoal,
  convertStatus,
  convertStatusUrl,
  HiveRequestAcceptanceStatus,
  SlaStatus,
  Status,
  ufAbbreviation,
} from '../../../../api/enumerations';
import { WorkOrderData } from '../../../../api/workOrders/types';
import {
  IconArrowDownwardMS,
  IconArrowUpwardMS,
  IconArrowBackMS,
  IconArrowForwardIosMS,
  IconSearchMS,
} from '../../../../constants/icons';
import { formatDate } from '../../../../helpers';
import { QuickFilters, QuickFilterKey } from './QuickFilters';
import { StatusChips } from './StatusChips';
import {
  BodyRow,
  EmptyStateBox,
  HeaderCell,
  PaginationControls,
  SlaBadge,
  StatusBadge,
  StyledTable,
  TableScroll,
  TableWrapper,
  Toolbar,
} from './styles';

interface TableViewProps {
  data: WorkOrderData[];
  loading: boolean;
}

type SortKey =
  | 'reference_number'
  | 'client_name'
  | 'status'
  | 'company'
  | 'evaluation'
  | 'goal'
  | 'schedule'
  | 'limit_finish';

interface ColumnDef {
  key: SortKey | 'inspector' | 'location' | 'actions';
  label: string;
  sortable: boolean;
  width?: string;
}

const COLUMNS: ColumnDef[] = [
  { key: 'reference_number', label: 'OS#', sortable: true, width: '80px' },
  { key: 'client_name', label: 'Cliente', sortable: true, width: '180px' },
  { key: 'status', label: 'Status', sortable: true, width: '160px' },
  { key: 'company', label: 'Empresa', sortable: true, width: '180px' },
  { key: 'inspector', label: 'Vistoriador', sortable: false, width: '160px' },
  { key: 'location', label: 'Localização', sortable: false, width: '200px' },
  { key: 'evaluation', label: 'Tipo de laudo', sortable: true, width: '160px' },
  { key: 'goal', label: 'Objetivo', sortable: true, width: '200px' },
  { key: 'schedule', label: 'Agendamento', sortable: true, width: '120px' },
  {
    key: 'limit_finish',
    label: 'Limite entrega',
    sortable: true,
    width: '160px',
  },
];

const PAGE_SIZE_OPTIONS = [25, 50, 100, 200];
const DEFAULT_PAGE_SIZE = 25;

const slaToneFor = (
  status: SlaStatus | null
): 'overdue' | 'near' | 'ok' | null => {
  if (status === SlaStatus.OVERDUE) return 'overdue';
  if (status === SlaStatus.NEAR_DEADLINE) return 'near';
  if (status === SlaStatus.ON_TRACK) return 'ok';
  return null;
};

const slaBadgeLabel = (
  row: WorkOrderData,
  tone: 'overdue' | 'near' | 'ok'
): string => {
  const days = row.sla_days_remaining ?? 0;
  if (tone === 'overdue') return `${Math.abs(days)}d atrasado`;
  if (tone === 'near') return `${days}d restante`;
  return 'no prazo';
};

const matchesQuickFilter = (
  row: WorkOrderData,
  filter: QuickFilterKey | null
): boolean => {
  if (!filter) return true;
  switch (filter) {
    case 'overdue':
      return row.sla_status === SlaStatus.OVERDUE;
    case 'near_deadline':
      return row.sla_status === SlaStatus.NEAR_DEADLINE;
    case 'no_inspector':
      return !row.inspection?.engineer_name;
    case 'scheduled_today': {
      const date = row.inspection?.schedule_at;
      if (!date) return false;
      return dayjs(date).isSame(dayjs(), 'day');
    }
    case 'rejected_by_bank':
      return (
        row.hive_request?.acceptance_status ===
        HiveRequestAcceptanceStatus.REJECTED
      );
    default:
      return true;
  }
};

interface AllCounters {
  status: Partial<Record<Status, number>>;
  quick: Partial<Record<QuickFilterKey, number>>;
}

const buildCounters = (rows: WorkOrderData[]): AllCounters => {
  const status: Partial<Record<Status, number>> = {};
  const quick: Partial<Record<QuickFilterKey, number>> = {
    overdue: 0,
    near_deadline: 0,
    scheduled_today: 0,
    no_inspector: 0,
    rejected_by_bank: 0,
  };
  const todayStart = dayjs().startOf('day');
  rows.forEach((row) => {
    status[row.status] = (status[row.status] ?? 0) + 1;
    if (row.sla_status === SlaStatus.OVERDUE) {
      quick.overdue = (quick.overdue ?? 0) + 1;
    }
    if (row.sla_status === SlaStatus.NEAR_DEADLINE) {
      quick.near_deadline = (quick.near_deadline ?? 0) + 1;
    }
    if (!row.inspection?.engineer_name) {
      quick.no_inspector = (quick.no_inspector ?? 0) + 1;
    }
    if (
      row.inspection?.schedule_at &&
      dayjs(row.inspection.schedule_at).isSame(todayStart, 'day')
    ) {
      quick.scheduled_today = (quick.scheduled_today ?? 0) + 1;
    }
    if (
      row.hive_request?.acceptance_status ===
      HiveRequestAcceptanceStatus.REJECTED
    ) {
      quick.rejected_by_bank = (quick.rejected_by_bank ?? 0) + 1;
    }
  });
  return { status, quick };
};

const sortValueFor = (row: WorkOrderData, key: SortKey): string | number => {
  switch (key) {
    case 'reference_number':
      return row.reference_number;
    case 'client_name':
      return row.client_name?.toLowerCase() ?? '';
    case 'status':
      return convertStatus(row.status);
    case 'company':
      return row.worker_company?.name?.toLowerCase() ?? '';
    case 'evaluation':
      return convertEvaluationType(row.evaluation_type);
    case 'goal':
      return convertReportGoal(row.report_goal);
    case 'schedule':
      return row.inspection?.schedule_at
        ? dayjs(row.inspection.schedule_at).valueOf()
        : Number.MAX_SAFE_INTEGER;
    case 'limit_finish':
      // The cell shows date + SLA badge derived from sla_days_remaining;
      // sorting by limit_finish_at is useless because the field arrives
      // empty for most OS, collapsing every row to MAX_SAFE_INTEGER.
      // sla_days_remaining is what the badge shows and carries urgency
      // semantics (negative = days overdue, positive = days left).
      if (typeof row.sla_days_remaining === 'number') {
        return row.sla_days_remaining;
      }
      return Number.MAX_SAFE_INTEGER;
    default:
      return '';
  }
};

const ariaSortFor = (
  isActive: boolean,
  direction: 'asc' | 'desc'
): 'ascending' | 'descending' | undefined => {
  if (!isActive) return undefined;
  return direction === 'asc' ? 'ascending' : 'descending';
};

const formatLocation = (row: WorkOrderData): string => {
  const uf = ufAbbreviation[row.uf];
  if (row.city && uf) return `${row.city} / ${uf}`;
  if (row.city) return row.city;
  if (uf) return uf;
  return '—';
};

export function TableView({ data, loading }: TableViewProps): JSX.Element {
  const navigate = useNavigate();
  const [sortKey, setSortKey] = useState<SortKey>('limit_finish');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const [quickFilter, setQuickFilter] = useState<QuickFilterKey | null>(null);
  const [statusFilter, setStatusFilter] = useState<Status[]>([]);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);

  const counters = useMemo(() => buildCounters(data), [data]);

  const filtered = useMemo(
    () =>
      data.filter((row) => {
        if (statusFilter.length > 0 && !statusFilter.includes(row.status)) {
          return false;
        }
        return matchesQuickFilter(row, quickFilter);
      }),
    [data, quickFilter, statusFilter]
  );

  const sorted = useMemo(() => {
    const copy = [...filtered];
    copy.sort((a, b) => {
      const av = sortValueFor(a, sortKey);
      const bv = sortValueFor(b, sortKey);
      if (av < bv) return sortDirection === 'asc' ? -1 : 1;
      if (av > bv) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
    return copy;
  }, [filtered, sortKey, sortDirection]);

  const pagedStart = page * pageSize;
  const paged = sorted.slice(pagedStart, pagedStart + pageSize);
  const totalPages = Math.max(1, Math.ceil(sorted.length / pageSize));

  const toggleSort = (key: SortKey): void => {
    if (sortKey === key) {
      setSortDirection((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDirection('asc');
    }
    setPage(0);
  };

  const handleSortKeyDown = (
    e: KeyboardEvent<HTMLTableCellElement>,
    key: SortKey
  ): void => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      toggleSort(key);
    }
  };

  const handleQuickFilter = (key: QuickFilterKey | null): void => {
    setQuickFilter(key);
    setPage(0);
  };

  const navigateToRow = (row: WorkOrderData): void => {
    navigate(`/home/property/${row.id}/${convertStatusUrl(row.status)}`);
  };

  const handleRowKeyDown = (
    e: KeyboardEvent<HTMLTableRowElement>,
    row: WorkOrderData
  ): void => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      navigateToRow(row);
    }
  };

  return (
    <TableWrapper>
      <StatusChips
        selected={statusFilter}
        onChange={(next) => {
          setStatusFilter(next);
          setPage(0);
        }}
        counters={counters.status}
      />
      <QuickFilters
        active={quickFilter}
        onChange={handleQuickFilter}
        counters={counters.quick}
      />
      <Toolbar>
        <span>
          {loading
            ? 'carregando OS...'
            : `${sorted.length} OS${sorted.length === 1 ? '' : 's'}${
                quickFilter ? ' (filtradas)' : ''
              }`}
        </span>
        <PaginationControls>
          <span>linhas por página</span>
          <Select
            size="small"
            value={pageSize}
            onChange={(e) => {
              setPageSize(Number(e.target.value));
              setPage(0);
            }}
            sx={{ fontSize: '13px' }}
            inputProps={{ 'aria-label': 'linhas por página' }}
          >
            {PAGE_SIZE_OPTIONS.map((opt) => (
              <MenuItem key={opt} value={opt}>
                {opt}
              </MenuItem>
            ))}
          </Select>
          <span>
            página {page + 1} de {totalPages}
          </span>
          <IconButton
            size="small"
            aria-label="página anterior"
            onClick={() => setPage((p) => Math.max(0, p - 1))}
            disabled={page === 0}
          >
            {IconArrowBackMS}
          </IconButton>
          <IconButton
            size="small"
            aria-label="próxima página"
            onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
            disabled={page >= totalPages - 1}
          >
            {IconArrowForwardIosMS}
          </IconButton>
        </PaginationControls>
      </Toolbar>
      <TableScroll>
        {loading && <LinearProgress />}
        <TableContainer>
          <StyledTable size="small" stickyHeader>
            <TableHead>
              <TableRow>
                {COLUMNS.map((col) => {
                  const isSortable = col.sortable;
                  const isActive = isSortable && sortKey === col.key;
                  return (
                    <HeaderCell
                      key={col.key}
                      sortable={isSortable}
                      className={isActive ? 'active' : undefined}
                      style={{ width: col.width }}
                      role={isSortable ? 'columnheader button' : 'columnheader'}
                      tabIndex={isSortable ? 0 : -1}
                      aria-sort={ariaSortFor(isActive, sortDirection)}
                      onClick={
                        isSortable
                          ? () => toggleSort(col.key as SortKey)
                          : undefined
                      }
                      onKeyDown={
                        isSortable
                          ? (e) => handleSortKeyDown(e, col.key as SortKey)
                          : undefined
                      }
                    >
                      {col.label}
                      {isActive && (
                        <span className="sort-icon" aria-hidden="true">
                          {sortDirection === 'asc'
                            ? IconArrowUpwardMS
                            : IconArrowDownwardMS}
                        </span>
                      )}
                    </HeaderCell>
                  );
                })}
              </TableRow>
            </TableHead>
            <TableBody>
              {paged.length === 0 && !loading && (
                <TableRow>
                  <TableCell colSpan={COLUMNS.length}>
                    <EmptyStateBox>
                      {IconSearchMS}
                      <p>nenhuma OS encontrada com os filtros aplicados</p>
                    </EmptyStateBox>
                  </TableCell>
                </TableRow>
              )}
              {paged.map((row) => {
                const tone = slaToneFor(row.sla_status);
                const detailUrl = `/home/property/${row.id}/${convertStatusUrl(
                  row.status
                )}`;
                return (
                  <BodyRow
                    key={row.id}
                    role="link"
                    tabIndex={0}
                    aria-label={`OS ${row.reference_number}, ${
                      row.client_name || 'sem cliente'
                    }, ${convertStatus(row.status)}`}
                    onClick={() => navigateToRow(row)}
                    onKeyDown={(e) => handleRowKeyDown(e, row)}
                  >
                    <TableCell>
                      <Link to={detailUrl} onClick={(e) => e.stopPropagation()}>
                        {row.reference_number}
                      </Link>
                    </TableCell>
                    <TableCell>{row.client_name || '—'}</TableCell>
                    <TableCell>
                      <StatusBadge>{convertStatus(row.status)}</StatusBadge>
                    </TableCell>
                    <TableCell>{row.worker_company?.name || '—'}</TableCell>
                    <TableCell>
                      {row.inspection?.engineer_name || '—'}
                    </TableCell>
                    <TableCell>{formatLocation(row)}</TableCell>
                    <TableCell>
                      {convertEvaluationType(row.evaluation_type) || '—'}
                    </TableCell>
                    <TableCell>
                      {convertReportGoal(row.report_goal) || '—'}
                    </TableCell>
                    <TableCell>
                      {row.inspection?.schedule_at
                        ? formatDate(row.inspection.schedule_at)
                        : '—'}
                    </TableCell>
                    <TableCell>
                      {row.limit_finish_at && (
                        <span style={{ marginRight: 6 }}>
                          {formatDate(row.limit_finish_at)}
                        </span>
                      )}
                      {tone && (
                        <SlaBadge tone={tone}>
                          {slaBadgeLabel(row, tone)}
                        </SlaBadge>
                      )}
                    </TableCell>
                  </BodyRow>
                );
              })}
            </TableBody>
          </StyledTable>
        </TableContainer>
      </TableScroll>
    </TableWrapper>
  );
}
